<script setup lang="ts">
import type { Database } from '~~/types/database.types'
import type { DbUserAccount } from '~~/types/account.types'
import type { InternalApiError } from '~~/types/error.types'
import type { SearchData } from '~~/types/eodhd.types'
import type { EmojiExt } from 'vue3-emoji-picker'

import { Combobox, ComboboxButton, ComboboxInput, ComboboxOptions, ComboboxOption } from '@headlessui/vue'
import { ChevronUpDownIcon } from '@heroicons/vue/20/solid'
import { XCircleIcon } from '@heroicons/vue/24/outline'
import { useDebounceFn, onClickOutside } from '@vueuse/core'

const props = defineProps<{
  portfolio?: DbUserAccount
}>()

const emit = defineEmits<{
  (event: 'cancel'): void
  (event: 'portfolioCreated', accountId: number): void
  (event: 'portfolioUpdated'): void
}>()

const user = useSupabaseUser()
const { $Sentry } = useNuxtApp()
const supabaseClient = useSupabaseClient<Database>()
const { addSuccessNotification } = useNotifications()

const isLoading = ref(false)
const errorMessage = ref<string | null>(null)

// Get existing user accounts
const userAccounts = await useUserAccounts()
watch(userAccounts, () => {
  console.log('watched userAccounts inside ManualPortfolioManager.vue', userAccounts.value)
})

const searchQuery = ref('')
const isSearching = ref(false)
const searchResults = ref<SearchData[]>([])

// Debounced search function
const debouncedSearch = useDebounceFn(async (query: string) => {
  if (!query || query.length < 2) {
    searchResults.value = []
    isSearching.value = false
    return
  }

  isSearching.value = true
  try {
    searchResults.value = await $fetch<SearchData[]>('/api/v1/search', {
      query: { query }
    })
  } catch (error) {
    console.error('Search error:', error)
    searchResults.value = []
  } finally {
    isSearching.value = false
  }
}, 300)

// Watch search query changes
watch(searchQuery, (newQuery) => {
  isSearching.value = true
  debouncedSearch(newQuery)
})

// Group search results by the order of the "exchanges" array
const groupedSearchResultsByExchange = computed(() => {
  return exchanges.reduce((groups, exchange) => {
    const resultsForExchange: SearchData[] = searchResults.value.filter(stock => exchange.eodhdCode === stock.Exchange)
    if (exchange.eodhdCode === 'US') {
      const countryName = getRegionNameByAlphaCode(exchange.countryISO2)
      const categoryName = `${countryName} - USA Stocks (NASDAQ, NYSE, OTC)`
      if (!groups[categoryName]) {
        groups[categoryName] = []
        groups[categoryName].push(...resultsForExchange)
      }
    } else {
      if (resultsForExchange.length > 0) {
        const countryName = getRegionNameByAlphaCode(exchange.countryISO2)
        const categoryName = `${countryName} - ${exchange.name}`
        if (!groups[categoryName]) {
          groups[categoryName] = []
        }
        groups[categoryName].push(...resultsForExchange)
      }
    }
    return groups
  }, {} as Record<string, SearchData[]>)
})

// Add these refs after other refs
const positions = ref<Array<{
  stock: SearchData | undefined
  quantity: number | null
  unitPrice: number | null
}>>([])

// Always add a new position at first
addNewPosition()

// Add this function after other functions
function addNewPosition() {
  positions.value.push({
    stock: undefined,
    quantity: null,
    unitPrice: null,
  })
}

// Reset a position to its initial state
function resetPosition(index: number) {
  if (positions.value[index]) {
    positions.value[index] = {
      stock: undefined,
      quantity: null,
      unitPrice: null,
    }
  }
}

// Add a new position if the last index is called
function addNewPositionIfLastIndex(index: number) {
  if (index === positions.value.length - 1) {
    addNewPosition()
  }
}

/* Adding a new manual portfolio */

// Function to validate portfolio name
function isNameAlreadyUsed(name: string, currentPortfolioId?: number) {
  // Getting all the manual accounts that are not the current portfolio
  const manualAccounts = userAccounts.value.filter(account => account.broker === 'manual' && account.id !== currentPortfolioId)

  // Checking if the name is already used by another manual account
  const isNameAlreadyUsed = manualAccounts.some(account => account.name.toLowerCase().trim() === name.toLowerCase().trim())

  return isNameAlreadyUsed
}

// Refs to handle the emoji picker and portfolio name
const isEmojiPickerOpen = ref(false)
const selectedEmoji = ref(props.portfolio?.emoji ?? '🚀')
const portfolioName = ref(props.portfolio?.name ?? '')

// Function to select an emoji
const onSelectEmoji = (emoji: EmojiExt) => {
  selectedEmoji.value = emoji.i
  isEmojiPickerOpen.value = false
}

// Close the emoji picker when clicking outside it
const emojiPickerRef = ref<HTMLElement | null>(null)
onClickOutside(emojiPickerRef, () => {
  isEmojiPickerOpen.value = false
})

// Function to create and update the manual portfolio
async function upsertManualPortfolio() {
  isLoading.value = true
  errorMessage.value = null

  try {
    const userId = user.value?.sub
    if (!userId) {
      throw new Error('User id is not valid')
    }

    // Check if name already exists on another manual account
    const cleanName = portfolioName.value.trim()
    if (isNameAlreadyUsed(cleanName, props.portfolio?.id)) {
      errorMessage.value = 'Ce nom est déjà utilisé par un autre portefeuille manuel.'
      return
    } else if (cleanName.length === 0) {
      errorMessage.value = 'Veuillez choisir un nom pour votre portefeuille.'
      return
    }

    // Validate positions
    const positionsToSave = positions.value.filter(position => position.stock !== undefined && position.stock !== null)
    if (props.portfolio === undefined && positionsToSave.length === 0) {
      errorMessage.value = 'Veuillez remplir au moins une position pour créer votre portefeuille.'
      return
    }
    const positionsWithUndefinedValues = positionsToSave.filter(position => !position.quantity || !position.unitPrice)
    if (positionsWithUndefinedValues.length > 0) {
      errorMessage.value = 'Veuillez remplir toutes les valeurs pour chaque position de votre portefeuille.'
      return
    }

    let accountId: number
    const cleanEmoji = selectedEmoji.value === '' ? null : selectedEmoji.value

    if (props.portfolio) {
      // Only update the portfolio name/emoji if changed
      if (props.portfolio.name !== cleanName || props.portfolio.emoji !== cleanEmoji) {
        const { error } = await supabaseClient
          .from('user_accounts')
          .update({ 
            name: cleanName,
            emoji: cleanEmoji
          })
          .eq('id', props.portfolio.id)

        if (error) throw error
      }
      accountId = props.portfolio.id
    } else {      
      // Create new account (manual portfolio)
      const { data: newAccount, error } = await supabaseClient
        .from('user_accounts')
        .insert({
          user_id: userId,
          name: cleanName,
          broker: 'manual',
          emoji: cleanEmoji
        })
        .select('*')
        .single()

      if (error || !newAccount) throw error
      accountId = newAccount.id
    }

    // Only call the API if there are positions to save
    if (positionsToSave.length > 0) {
      try {
        await $fetch('/api/v1/manual-account-update-positions', {
          method: 'POST',
          body: {
            positions: positionsToSave,
            accountId
          }
        })
      } catch (error: any) {
        if (!props.portfolio) {
          // Only delete if this was a new portfolio
          await supabaseClient
            .from('user_accounts')
            .delete()
            .eq('id', accountId)
        }
        if (error && isApiError(error.data)) {
          const apiError: InternalApiError = error.data
          console.log('apiError', apiError)
          if (apiError.statusMessage === 'BaseError' && apiError.data.isUserError) {
            const searchData: SearchData = apiError.data.detail as SearchData
            errorMessage.value = getErrorMessage(apiError.data.errorName, apiError.data.errorCode) + `<br/><br/>Action concernée: ${searchData?.Code} - ${searchData?.Name} - ${getExchangeFromEodhdCode(searchData?.Exchange ?? '').name}`
          } else {
            errorMessage.value = getErrorMessageFromApiError(apiError)
          }
        } else {
          errorMessage.value = getErrorMessage('UnknownError')
        }
        return
      }
    }

    // Refresh user accounts
    await useUserAccounts(true)
    // await useLazyUserAccounts()

    addSuccessNotification({
      title: props.portfolio ? 'Portefeuille mis à jour' : 'Portefeuille créé',
      message: props.portfolio
        ? `Le portefeuille "${cleanName}" a été mis à jour avec succès.`
        : `Le portefeuille "${cleanName}" a été créé avec succès.`
    })

    props.portfolio ? emit('portfolioUpdated') : emit('portfolioCreated', accountId)
  } catch (e: any) {
    $Sentry.captureException(e)
    errorMessage.value = props.portfolio
      ? 'Une erreur est survenue lors de la mise à jour du portefeuille.'
      : 'Une erreur est survenue lors de la création du portefeuille.'
  } finally {
    isLoading.value = false
  }
}
</script>

<template>
  <div class="">
    <PageSubtitle class="text-lg">
      {{ portfolio ? 'Modifier votre portefeuille' : 'Créer un nouveau portefeuille manuel' }}
    </PageSubtitle>
    <p class="mt-2 text-sm text-gray-600">
      {{ portfolio ? 'Ajouter de nouvelles positions à votre portefeuille' : 'Définir les positions de votre portefeuille' }}
    </p>
    <FormKit
      type="form"
      @submit="upsertManualPortfolio"
      id="create-manual-portfolio-form"
      :actions="false"
      form-class="mt-4 space-y-3"
    >

      <div class="relative max-w-xs">
        <label class="block text-sm/6 font-medium text-gray-900">Nom du portefeuille</label>
        <div class="mt-2 flex">
          <button
            type="button"
            @click="isEmojiPickerOpen === false ? isEmojiPickerOpen = true : null"
            class="flex shrink-0 items-center rounded-l-md bg-white px-3 text-base text-gray-500 outline outline-1 -outline-offset-1 outline-gray-300 hover:bg-gray-50 sm:text-sm/6"
          >
            {{ selectedEmoji || '' }}
          </button>
          
          <input
            v-model="portfolioName"
            type="text"
            class="-ml-px block w-full grow rounded-r-md bg-white px-3 py-1.5 text-base text-gray-900 outline-none outline-1 -outline-offset-1 outline-gray-300 placeholder:text-gray-400 sm:text-sm/6"
            placeholder="Choissisez un nom"
          />
        </div>
        <div 
          v-if="isEmojiPickerOpen" 
          class="absolute z-50 mt-1"
        >
          <ClientOnly>
            <NuxtEmojiPicker
              ref="emojiPickerRef"
              @close="isEmojiPickerOpen = false"
              :native="true"
              @select="onSelectEmoji"
              :hide-search="true"
              theme="light"
              :group-names="{
                'smileys_people': 'Sourires et gens',
                'animals_nature': 'Animaux et nature',
                'food_drink': 'Nourriture et boisson',
                'activities': 'Activités',
                'travel_places': 'Lieux et voyages',
                'objects': 'Objets',
                'symbols': 'Symboles',
                'flags': 'Drapeaux'
              }"
              :static-texts="{
                skinTone: 'Teint de la peau'
              }"
            />
          </ClientOnly>
        </div>
      </div>

      <div class="mt-8">
        <div class="flow-root">
          <div class="-mx-4 -my-2 overflow-x-auto sm:-mx-6 lg:-mx-8">
            <div class="inline-block min-w-full py-2 align-middle sm:px-6 lg:px-8">
              <table class="min-w-full divide-y divide-gray-300">
                <thead>
                  <tr>
                    <th scope="col" class="py-3.5 pl-4 pr-3 text-left text-sm font-semibold text-gray-900 sm:pl-0">Nouvelle position</th>
                    <th scope="col" class="px-3 py-3.5 text-left text-sm font-semibold text-gray-900">Quantité</th>
                    <th scope="col" class="px-3 py-3.5 text-left text-sm font-semibold text-gray-900">Coût par action</th>
                    <th scope="col" class="relative py-3.5 pl-3 pr-4 sm:pr-0">
                      <span class="sr-only">Actions</span>
                    </th>
                  </tr>
                </thead>
                <tbody class="divide-y divide-gray-200">
                  <tr v-for="(position, index) in positions" :key="index">
                    <td class="py-4 pl-4 pr-3 text-sm sm:pl-0">
                      <Combobox v-model="position.stock" nullable as="div" @update:model-value="position.unitPrice = position.stock?.previousClose ?? null">
                        <div class="relative">
                          <ComboboxInput
                            class="w-full rounded-md border-0 bg-white py-1.5 pl-3 pr-10 text-gray-900 shadow-sm ring-1 ring-inset ring-gray-300 focus:ring-2 focus:ring-inset focus:ring-sky-blue-600 sm:text-sm sm:leading-6"
                            :class="{ 'pl-9': position.stock?.Exchange }"
                            :display-value="(stock: any) => !stock || !position.stock ? '' : stock?.Code + ' - ' + truncate(stock?.Name, 35)"
                            placeholder="Ticker, nom, ISIN..."
                            @change="searchQuery = $event.target.value"
                            @focus="addNewPositionIfLastIndex(index)"
                          />
                          <ComboboxButton class="absolute inset-y-0 right-0 flex items-center pr-2">
                            <ChevronUpDownIcon class="h-5 w-5 text-gray-400" aria-hidden="true" />
                          </ComboboxButton>
                          <div v-if="position.stock?.Exchange" class="absolute inset-y-0 left-0 flex items-center pl-3">
                            <img :src="`https://flagcdn.com/80x60/${getExchangeFromEodhdCode(position.stock?.Exchange).countryISO2.toLowerCase()}.png`" alt="Drapeau du pays" class="h-3 inline-block mr-2 align-middle" />
                          </div>
                        </div>

                        <Transition
                          enter-active-class="transition duration-100 ease-out"
                          enter-from-class="transform scale-95 opacity-0"
                          enter-to-class="transform scale-100 opacity-100"
                          leave-active-class="transition duration-75 ease-out"
                          leave-from-class="transform scale-100 opacity-100"
                          leave-to-class="transform scale-95 opacity-0"
                        >
                          <ComboboxOptions class="max-w-lg absolute z-10 mt-1 max-h-60 w-full overflow-auto rounded-md bg-white py-1 text-base shadow-lg ring-1 ring-black ring-opacity-5 focus:outline-none sm:text-sm">
                            <div v-if="isSearching" class="relative cursor-default select-none px-4 py-2 text-gray-700">
                              Recherche en cours...
                            </div>
                            <div 
                              v-else-if="searchResults.length === 0 && searchQuery.length >= 2" 
                              class="relative cursor-default select-none px-4 py-2 text-gray-700"
                            >
                              Aucun résultat trouvé.
                            </div>
                            <div 
                              v-else-if="searchQuery.length < 2" 
                              class="relative cursor-default select-none px-4 py-2 text-gray-700"
                            >
                              Veuillez saisir au moins 2 caractères...
                            </div>
                            <template v-else v-for="(stocks, country) in groupedSearchResultsByExchange" :key="country">
                              <div class="px-2.5 py-2 text-xs text-gray-500 font-semibold">
                                <img :src="`https://flagcdn.com/80x60/${getExchangeFromEodhdCode(stocks[0]?.Exchange ?? '').countryISO2.toLowerCase()}.png`" alt="Drapeau du pays" class="h-3 inline-block mr-2 align-middle" />
                                <span class="inline-block align-middle">{{ country }}</span>
                              </div>
                              <ComboboxOption
                                v-for="stock in stocks"
                                :key="stock.Code + stock?.Exchange"
                                v-slot="{ selected, active }"
                                :value="stock"
                                as="template"
                              >
                                <li
                                  class="relative cursor-pointer select-none py-2 pl-3 pr-9"
                                  :class="{
                                    'bg-sky-blue-600 text-white': active,
                                    'text-gray-900': !active
                                  }"
                                >
                                  <div class="flex items-center">
                                    <span 
                                      class="truncate font-medium"
                                      :class="{ 'font-semibold': selected }"
                                    >
                                      {{ stock.Code }}
                                      <!-- {{ stock.Code }}.{{ getExchangeFromEodhdCode(stock.Exchange).mic }} -->
                                    </span>
                                    <span 
                                      class="ml-2 truncate text-gray-500"
                                      :class="{ 'text-sky-blue-200': active }"
                                    >
                                      {{ truncate(stock.Name, 40) }}
                                    </span>
                                  </div>
                                </li>
                              </ComboboxOption>
                            </template>
                          </ComboboxOptions>
                        </Transition>
                      </Combobox>
                    </td>
                    <td class="px-3 py-4 text-sm text-gray-500 max-w-[100px]">
                      <FormKit
                        type="number"
                        number="float"
                        v-model="(position.quantity as number | undefined)"
                        outer-class="!mb-0"
                        :min="0"
                        step="any"
                        @focus="addNewPositionIfLastIndex(index)"
                      />
                    </td>
                    <td class="px-3 py-4 text-sm text-gray-500 max-w-[100px]">
                      <div class="relative rounded-md shadow-sm">
                        <FormKit
                          type="number"
                          number="float"
                          v-model="(position.unitPrice as number | undefined)"
                          input-class="pr-12"
                          outer-class="!mb-0"
                          :min="0"
                          step="any"
                          @focus="addNewPositionIfLastIndex(index)"
                        />
                        <span 
                          v-if="position.stock?.Currency"
                          class="absolute inset-y-0 right-0 flex items-center pr-3 text-gray-500 pointer-events-none"
                        >
                          {{ position.stock.Currency }}
                        </span>
                      </div>
                    </td>
                    <td class="relative py-4 px-3 text-right text-sm font-medium sm:pr-0">
                      <button
                        type="button"
                        :class="(position.stock || position.quantity || position.unitPrice) ? 'visible' : 'invisible'"
                        @click="resetPosition(index)"
                        class="text-gray-500 hover:text-gray-900"
                      >
                        <XCircleIcon class="h-5 w-5" />
                        <span class="sr-only">Supprimer</span>
                      </button>
                    </td>
                  </tr>
                </tbody>
              </table>
            </div>
          </div>
        </div>
      </div>

      <Alert v-if="errorMessage" type="error">
        <p v-html="errorMessage"></p>
      </Alert>

      <div class="pt-2 gap-x-2 flex items-center">
        <Button type="submit" theme="primary" :is-loading="isLoading">
          {{ portfolio ? 'Mettre à jour le portefeuille' : 'Créer le portefeuille' }}
        </Button>
        <Button type="reset" @click="emit('cancel')" theme="neutral" :disabled="isLoading">
          Annuler
        </Button>
      </div>
    </FormKit>
  </div>
</template> 